// @flow
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { withLeaflet } from 'react-leaflet';
import { History, withRouter } from 'react-router-dom';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { get } from 'lodash';
import { Box } from 'grommet';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import { CONTENT_TOOLTIP_PANE_NAME } from 'esm-web-pro/containers/pages/network/NetworkMapEditor';
import { type GISLine } from './GisLine';


type Props = {
    id: string,
    line: GISLine,
    leaflet: LeafletContext,
    onClose: Function,
    updateObject: Function,
    onSave: Function,
    map: Object,
    editor: Object,
    history: History,
    match: Object,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    setSensitivity: (key: string, sensitivity: boolean) => void,
}

const GisLineEditor = (props: Props) => {
    const {
        leaflet: { map }, line, onClose, updateObject, onSave, map: { uuid: mapUuid }, editor, history, match,
        setSensitivity, id, confirm, intl: { formatMessage },
    } = props;

    const layerRef = React.useRef(null);
    const resetLayer = React.useRef(true);

    React.useEffect(() => {
        if (!editor.state.loading) {
            if (!map) {
                throw Error('GisLineEditor should be rendered as a child of leaflet Map component.');
            }
            map.eachLayer((l) => {
                if (l.feature && l.feature.id === line.uuid) {
                    l.pm.enable();
                    if (!layerRef.current || l._leaflet_id !== layerRef.current._leaflet_id) {
                        layerRef.current = l;

                        if (layerRef.current.contentTooltip) {
                            map.removeLayer(layerRef.current.contentTooltip);
                        }
                    }
                }
            });
        }
    }, [layerRef.current]);

    React.useEffect(() => () => {
        if (resetLayer.current && layerRef.current) {
            const lineCoords = line.path.coordinates.map((c) => [c[1], c[0]]);
            layerRef.current.setLatLngs(lineCoords);
            layerRef.current.pm.disable();
            if (layerRef.current.contentTooltip && map.getPane(CONTENT_TOOLTIP_PANE_NAME)) {
                map.addLayer(layerRef.current.contentTooltip);
            }
        }
    }, []);

    const handleClose = () => {
        onClose();
        if (layerRef.current) {
            layerRef.current.pm.disable();
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const handleUpdate = (response: Object) => {
        // Unfocus gis line in the case when an active gis line is edited and after the change a draft gis line
        // is crated and which will have different feature id when it is added to the map. So if the active gis line
        // is not unfocused it will not be removed from the map
        editor.focus();
        onSave(response);
        handleClose();

        if (layerRef.current.contentTooltip) {
            map.addLayer(layerRef.current.contentTooltip.setLatLng(layerRef.current.getCenter()));
        }
    };

    const handleSave = () => {
        resetLayer.current = false;
        if (!layerRef.current) {
            throw Error('There is no corresponding layer on the map for this line.');
        }
        const data = {
            path: layerRef.current.toGeoJSON().geometry,
        };
        updateObject('gisLine', line.uuid, { ...data, map: mapUuid }).then((res) => {
            if (res.ok === false) {
                confirm({
                    message: res.data.non_field_errors[0],
                    actions: {
                        cancel: {
                            label: formatMessage(sidePanelMessages.okButton),
                            callback: () => {
                            },
                            type: 'negative',
                        },
                    },
                });
            } else {
                handleUpdate(res);
            }
        });
    };

    return (
        <>
            <Box justify="center" height="100%" pad={{ horizontal: 'medium' }}>
                <FormattedMessage
                    id="gisline-edit-warning"
                    defaultMessage="Please note that editing this trace will change the path
                     and length of all its content."
                />
            </Box>
            <ActionButtons onSave={handleSave} onClose={handleCancel} />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    updateObject: {
        send: (resourceName, objectId, data) => {
            const resource = get(apiClient.resources, resourceName);
            return resource.update(objectId, data);
        },
    },
});

export default connect(null, (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch)))(
    injectIntl(withConfirmation(withRouter(connectAPI(apiCallback)(withLeaflet(GisLineEditor))))),
);
